import { ArrowUpDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";
import { SORT_LABELS, SORT_VALUES, type SortValue } from "../shop.catalog";

export interface SortSelectProps {
  value: SortValue;
  onChange: (value: SortValue) => void;
}

/**
 * El control de orden del catálogo.
 *
 * Es un `<select>` nativo vestido de píldora, y no el menú a mano del
 * sistema actual. Aquel tenía que mantener por su cuenta el clic fuera,
 * Escape, las flechas del teclado y el foco al cerrar — cuatro cosas que
 * el nativo ya hace, y que en móvil además abre con la rueda del sistema.
 * A cambio se pierde poder pintar una palomita en la opción activa, que
 * es exactamente lo que un `<select>` ya comunica solo.
 */
export function SortSelect({ value, onChange }: SortSelectProps) {
  const { t } = useTranslation();

  return (
    <div className="relative shrink-0">
      <ArrowUpDown
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 left-3.5 my-auto h-3.5 w-3.5 text-ds-ink-3"
      />

      <select
        value={value}
        onChange={(event) => onChange(event.target.value as SortValue)}
        aria-label={t("Ordenar por")}
        className={cn(
          "h-10 w-full cursor-pointer appearance-none rounded-ds-pill border border-ds-border bg-white",
          "py-0 pl-9 pr-9 text-[12px] font-semibold text-ds-ink-3",
          "outline-none transition hover:border-ds-violet-pop hover:text-ds-violet-pop",
          "focus-visible:ring-2 focus-visible:ring-ds-violet-pop/50 focus-visible:ring-offset-2 focus-visible:ring-offset-ds-cream",
        )}
      >
        {SORT_VALUES.map((option) => (
          <option key={option} value={option}>
            {t(SORT_LABELS[option])}
          </option>
        ))}
      </select>
    </div>
  );
}
